/**
 * Feedback Service
 * Flutter source: lib/data/repository/feedback_repository.dart:55-58
 *   (`FeedbackRepository.getUsersFeedback`, a pass-through to the data source)
 *   + lib/data/data_sources/feedback_data_source.dart:43-45
 *   (`querySnapshot.docs.map((json) => InAppFeedback.fromJson(json.data()))`).
 *
 * Thin domain fetch+remap path for a page of in-app feedback: calls the
 * Firestore collection-group query (`firestoreService.getUsersFeedback`) and
 * maps the raw page JSON to `InAppFeedbackEntity[]` via `InAppFeedbackRemapper`.
 *
 * Mirrors Flutter where the data source returns the raw page
 * (`{ items, hasMore, lastFetchedItem }`) with NO filtering. Any dedup /
 * pagination accumulation lives in the SCREEN (web: the hook, Task 7) — see
 * lib/presentation/screens/feedback/users_feedback_screen.dart — and is
 * deliberately NOT in this service.
 *
 * Web parity reference: notificationsService.ts:52-81 (`fetchNotifications`).
 */
import { firestoreService } from '@/data/services/firestoreService';
import type { FirestoreService } from '@/data/services/firestoreService';
import { cloudStorageService } from '@/data/storage/remote/cloudStorageService';
import type { CloudStorageService } from '@/data/storage/remote/cloudStorageService';
import { InAppFeedbackRemapper } from '@/domain/remappers/feedback/inAppFeedbackRemapper';
import type { InAppFeedbackEntity } from '@/domain/entities/feedback/inAppFeedbackEntity';
import type { InAppFeedbackDto } from '@/data/dtos/feedback/inAppFeedbackDto';
import type { InAppFeedbackScreenshotDto } from '@/data/dtos/feedback/inAppFeedbackScreenshotDto';
import type { SaveFeedbackInput } from '@/domain/input/feedback/saveFeedbackInput';
import type { PageCursor } from '@/data/types/json';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class FeedbackService {
  private static instance: FeedbackService;
  private firestoreServiceInstance: FirestoreService;
  private cloudStorageServiceInstance: CloudStorageService;

  constructor(
    firestoreServiceInstance: FirestoreService = firestoreService,
    cloudStorageServiceInstance: CloudStorageService = cloudStorageService,
  ) {
    this.firestoreServiceInstance = firestoreServiceInstance;
    this.cloudStorageServiceInstance = cloudStorageServiceInstance;
  }

  /**
   * Singleton accessor. Use cases should default to this instead of
   * constructing a fresh service. The public constructor is retained for test
   * injection of a stub `FirestoreService`.
   */
  static getInstance(): FeedbackService {
    if (!this.instance) {
      this.instance = new FeedbackService();
    }
    return this.instance;
  }

  /**
   * Fetch a paginated page of users' in-app feedback, newest-first.
   *
   * Thin fetch+remap: returns the raw page mapped to entities. NO filter, NO
   * dedup — those belong to the hook (Task 7). On error, mirrors the Flutter
   * data-source catch (feedback_data_source.dart:50-52) which falls back to the
   * default/empty response.
   */
  async getUsersFeedback(
    cursor: PageCursor | undefined,
    limit: number,
  ): Promise<{ items: InAppFeedbackEntity[]; hasMore: boolean; cursor?: PageCursor }> {
    try {
      const response = await this.firestoreServiceInstance.getUsersFeedback(
        cursor,
        limit,
      );

      const items = InAppFeedbackRemapper.fromDtoList(
        InAppFeedbackRemapper.fromJsonList(response.items),
      );

      return {
        items,
        hasMore: response.hasMore,
        cursor: response.cursor,
      };
    } catch (error) {
      appLogger.error(
        'Error fetching users feedback',
        'FeedbackService.getUsersFeedback',
        { error: error as Error },
      );
      return { items: [], hasMore: false };
    }
  }

  /**
   * Persist one in-app feedback document — the web equivalent of Flutter's
   * `FeedbackRepository.saveFeedback` (feedback_repository.dart:15-53).
   *
   * Orchestration (parity, in order):
   *   1. Guard on the auth-gated caller uid; a falsy id short-circuits to
   *      `false`, mirroring Flutter's null-firebase-user guard
   *      (feedback_repository.dart:17-20).
   *   2. Upload each draft screenshot SEQUENTIALLY (mirroring Flutter's `for`,
   *      :24-42). Only when the upload returns a non-null persisted path is a
   *      `{ path, aspectRatio }` screenshot appended (:33-41); a failed upload
   *      is SILENTLY skipped. The accumulator is left `undefined` (never `[]`)
   *      when no upload succeeds — Flutter's `screnshots` is a lazily-initialised
   *      `List<InAppFeedbackScreenshot>?` (:22, :39) that stays `null` when none
   *      succeed — so `toJson` OMITS the `screenshots` field entirely.
   *   3. Build the `InAppFeedbackDto` (uuid-v1 id, caller uid, description,
   *      screenshots?, `Date.now()` timestamp; :45-50) and write it as JSON via
   *      the Firestore proxy, returning the saved boolean (:51-52).
   *
   * Deferred (feature entry point): Flutter resizes each screenshot to a
   * width-540 JPEG BEFORE upload (app_file_service.dart:160-184, called at
   * feedback_repository.dart:25-27). The web has no client-side image
   * compression utility yet; it is a size optimization that does NOT change the
   * persisted document shape or cross-client rendering, so screenshots are
   * uploaded as-is (see the TODO marker at the upload site below).
   *
   * @param input - Caller uid + description + picked screenshot drafts.
   * @returns true when the document write succeeds; false on the uid guard or
   *   any error (parity with Flutter's `bool` return).
   */
  async saveFeedback(input: SaveFeedbackInput): Promise<boolean> {
    if (!input.firebaseUserId) {
      return false;
    }

    try {
      let screenshots: InAppFeedbackScreenshotDto[] | undefined;

      for (const draft of input.screenshots) {
        // TODO: @claude add a follow up task for this: client-side screenshot compression to width 540 before upload — Flutter lib/services/app_file_service.dart:160-184
        const uploadPath = `feedback/${input.firebaseUserId}/${Date.now()}`;
        const path = await this.cloudStorageServiceInstance.uploadFeedbackScreenshot(
          draft.file,
          uploadPath,
          'image/jpeg',
        );
        if (path != null) {
          (screenshots ??= []).push({
            path,
            aspectRatio: draft.aspectRatio,
          } satisfies InAppFeedbackScreenshotDto);
        }
      }

      const dto: InAppFeedbackDto = {
        id: generateUuidV1(),
        userId: input.firebaseUserId,
        description: input.description,
        screenshots,
        timestamp: Date.now(),
      };

      return await this.firestoreServiceInstance.saveFeedback(
        InAppFeedbackRemapper.toJson(dto),
      );
    } catch (error) {
      appLogger.error(
        'Error saving feedback',
        'FeedbackService.saveFeedback',
        { error: error as Error },
      );
      return false;
    }
  }
}

// Export singleton instance for convenience
export const feedbackService = FeedbackService.getInstance();
